import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/providers/AuthProvider';
import { roleSatisfies } from '@/lib/roleRank';
import { adminApi } from '@/lib/adminApi';
import { feedbackApi } from '@/lib/feedbackApi';
import { entryCompounds, entryGenes, filterSafetyEntries, type SafetyIndexEntry } from '@/lib/safetyIndex';
import { AdminPage, RegistryTable, StatusPill, EntityFormModal, FeedbackControl, type FieldSpec } from '@/admin';

const REGISTRY = 'safety_index';
const FIELDS: FieldSpec[] = [
  { name: 'entry', label: 'Entry (JSON)', type: 'json', hint: 'The full Safety Index entry object. Must satisfy the entry schema (entry_type, evidence, curation required).' },
];
const NEW_TEMPLATE = JSON.stringify({
  entry_id: '', entry_type: 'pharmacogenomic_single_gene', active: true,
  gene: '', snp: '', genotype_trigger: [], phenotype: '', compound: '', canonical_compound_id: '',
  action: '', action_detail: {}, rationale: '',
  evidence: { source: 'curated_seed' }, curation: { tier: '', reviewer: '', reviewed_at: '' },
}, null, 2);

export function SafetyIndexPage() {
  const qc = useQueryClient();
  const { role } = useAuth();
  const isAdmin = roleSatisfies(role ?? '', 'admin');
  const q = useQuery({ queryKey: ['safety-index'], queryFn: () => adminApi.safetyIndex.list() });
  const fb = useQuery({ queryKey: ['feedback-summary', REGISTRY], queryFn: () => feedbackApi.summary(REGISTRY) });
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState<{ editing: SafetyIndexEntry | null } | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const entries = q.data?.entries ?? [];
  const status = q.data?.status;
  const rows = filterSafetyEntries(entries, search);
  const refresh = () => void qc.invalidateQueries({ queryKey: ['safety-index'] });
  const refreshFb = () => void qc.invalidateQueries({ queryKey: ['feedback-summary', REGISTRY] });

  const save = useMutation({
    mutationFn: (v: Record<string, unknown>) => {
      const entry = v.entry as Record<string, unknown>;
      return modal?.editing
        ? adminApi.safetyIndex.update(String(entry.entry_id ?? modal.editing.entry_id), entry)
        : adminApi.safetyIndex.create(entry);
    },
    onSuccess: () => { setModal(null); setErr(null); refresh(); },
    onError: (e: unknown) => setErr(e instanceof Error ? e.message : 'Error'),
  });
  const del = useMutation({
    mutationFn: (id: string) => adminApi.safetyIndex.remove(id),
    onSuccess: refresh,
    onError: (e: unknown) => alert(e instanceof Error ? e.message : 'Error deleting entry'),
  });
  const toggle = useMutation({
    mutationFn: (id: string) => adminApi.safetyIndex.toggle(id),
    onSuccess: refresh,
    onError: (e: unknown) => alert(e instanceof Error ? e.message : 'Error toggling entry'),
  });

  return (
    <AdminPage
      title="Safety Index"
      description="Compound safety entries — pharmacogenomic form restrictions and contraindication gates the board applies. Flag an entry for review; admins can edit."
      actions={isAdmin ? <Button size="sm" onClick={() => { setErr(null); setModal({ editing: null }); }}>Add entry</Button> : undefined}
    >
      {status ? (
        <div className="mb-13 text-body-sm text-text-secondary">
          <strong className="text-text-primary">{status.entry_count}</strong> entries · {status.active_count} active
          {status.schema_version ? ` · schema v${status.schema_version}` : ''}
          {status.last_updated ? ` · ${status.last_updated}` : ''}
          {status.error ? <span className="text-flagged"> · load error: {status.error}</span> : ''}
        </div>
      ) : null}

      <RegistryTable<SafetyIndexEntry>
        columns={[
          { key: 'entry_id', header: 'Entry ID', className: 'font-mono text-[11px]', render: (e) => e.entry_id },
          { key: 'type', header: 'Type', className: 'text-[12px] text-text-secondary', render: (e) => e.entry_type?.replace('pharmacogenomic_', 'pgx ') || '—' },
          { key: 'genes', header: 'Gene(s)', className: 'font-mono text-[12px]', render: (e) => entryGenes(e) },
          { key: 'compounds', header: 'Compound(s)', className: 'text-[12px]', render: (e) => entryCompounds(e) },
          { key: 'action', header: 'Action', className: 'text-[12px]', render: (e) => e.action || '—' },
          { key: 'source', header: 'Source', className: 'text-[12px] text-text-secondary', render: (e) => e.evidence?.source || '—' },
          { key: 'tier', header: 'Tier', className: 'text-[12px] text-text-secondary', render: (e) => e.curation?.tier || '—' },
          { key: 'active', header: 'Active', render: (e) => (
            <button
              type="button" disabled={!isAdmin || toggle.isPending}
              onClick={() => toggle.mutate(e.entry_id)}
              title={isAdmin ? 'Toggle active' : undefined}
              className={isAdmin ? 'cursor-pointer' : 'cursor-default'}>
              <StatusPill on={e.active !== false} label={e.active !== false ? 'active' : 'inactive'} />
            </button>
          ) },
        ]}
        rows={rows}
        getRowKey={(e) => e.entry_id}
        count={entries.length}
        state={{ loading: q.isLoading, error: q.isError }}
        emptyText={search ? `No entries match "${search}"` : 'No Safety Index entries.'}
        search={{ value: search, onChange: setSearch, placeholder: 'Search by id, gene, compound, or action…' }}
        rowActions={(e) => (
          <div className="flex items-center gap-2">
            <FeedbackControl registry={REGISTRY} itemId={e.entry_id} itemLabel={entryCompounds(e)} openCount={fb.data?.[e.entry_id]?.open ?? 0} onChanged={refreshFb} />
            {isAdmin ? <Button variant="outline" size="sm" onClick={() => { setErr(null); setModal({ editing: e }); }}>Edit</Button> : null}
            {isAdmin ? <Button variant="destructive" size="sm" onClick={() => { if (confirm(`Delete Safety Index entry "${e.entry_id}"?`)) del.mutate(e.entry_id); }}>Delete</Button> : null}
          </div>
        )}
      />

      {modal ? (
        <EntityFormModal
          title={modal.editing ? `Edit ${modal.editing.entry_id}` : 'Add Safety Index entry'}
          mode={modal.editing ? 'edit' : 'add'}
          fields={FIELDS}
          initialValues={{ entry: modal.editing ? JSON.stringify(modal.editing, null, 2) : NEW_TEMPLATE }}
          error={err} busy={save.isPending}
          onClose={() => setModal(null)}
          onSubmit={(v) => save.mutate(v)}
        />
      ) : null}
    </AdminPage>
  );
}
